import { useState } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, Clock, Upload, X, FileText, ArrowLeft, Calendar, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { trackEvent } from '@/utils/analytics';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachedFile(file);
      toast.success(`File "${file.name}" attached`);
    }
  };

  const removeFile = () => {
    setAttachedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('subject', formData.subject);
      submitData.append('message', formData.message);
      if (attachedFile) {
        submitData.append('file', attachedFile);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          attachment_name: attachedFile?.name || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      // Track contact form submission
      trackEvent('generate_lead', {
        event_category: 'engagement',
        event_label: 'contact_form_submission'
      });

      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setAttachedFile(null);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Previous Page
        </button>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-black uppercase mb-4">GET IN TOUCH</h1>
            <p className="text-lg text-muted-foreground">
              Have questions? We'd love to hear from you.
            </p>
          </div>

          {/* Experience Centre Section */}
          <div className="mb-12 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-2 border-primary/30 rounded-xl p-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Map */}
              <div className="lg:w-1/2">
                <h2 className="text-2xl font-black uppercase mb-4 flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-primary" />
                  EXPERIENCE CENTRE
                </h2>
                <div className="rounded-lg overflow-hidden border-2 border-primary/20 shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.6694!2d72.8293953!3d19.140858!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA4JzI3LjEiTiA3MsKwNDknNTUuMSJF!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Modular Furniture Store Experience Centre Location"
                  />
                </div>
                <a 
                  href="https://www.google.com/maps/place/19%C2%B008'27.1%22N+72%C2%B049'55.1%22E/@19.140858,72.8293953,17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 text-sm text-primary hover:underline font-medium"
                >
                  <MapPin className="w-4 h-4" />
                  Open in Google Maps
                </a>
              </div>

              {/* Experience Centre Details */}
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg p-6 border border-border shadow-sm h-full">
                  <h3 className="font-bold text-xl mb-4 text-foreground">Modular Furniture Store</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-muted-foreground">
                          A-611, Crystal Plaza Premises,<br />
                          New Link Road,<br />
                          Andheri (W),<br />
                          Mumbai 400053
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Call:</p>
                        <p className="text-muted-foreground">
                          <a href="tel:+919594685818" className="hover:text-primary">9594685818</a> / <a href="tel:+919820042100" className="hover:text-primary">9820042100</a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Email:</p>
                        <a href="mailto:hello@modularfurniturestore.com" className="text-muted-foreground hover:text-primary">
                          hello@modularfurniturestore.com
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Appointment Notice */}
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-amber-800 text-sm uppercase">By Appointment Only</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Visits to our Experience Centre are strictly by pre-booked appointments.
                        </p>
                        <p className="text-sm text-amber-700 mt-2">
                          <strong>Book your visit:</strong><br />
                          <a href="tel:+919594685818" className="hover:underline">9594685818</a> / <a href="tel:+919820042100" className="hover:underline">9820042100</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white border border-border rounded-none p-8">
                <h2 className="text-2xl font-black uppercase mb-6">CONTACT INFORMATION</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold uppercase text-sm mb-1">ADDRESS</h3>
                      <p className="text-sm text-muted-foreground">
                        A-611, Crystal Plaza Premises,<br />
                        New Link Road, Andheri (W),<br />
                        Mumbai 400053
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold uppercase text-sm mb-1">PHONE</h3>
                      <p className="mono text-sm">+91 95946 85818</p>
                      <p className="mono text-sm">+91 98200 42100</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold uppercase text-sm mb-1">EMAIL</h3>
                      <p className="mono text-sm">hello@modularfurniturestore.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold uppercase text-sm mb-1">BUSINESS HOURS</h3>
                      <p className="text-sm text-muted-foreground">
                        Monday - Saturday: 9:00 AM - 6:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <div className="bg-accent border border-accent rounded-none p-8">
                <h3 className="text-xl font-black uppercase mb-4 text-accent-foreground">WHY CONTACT US?</h3>
                <ul className="space-y-3 text-sm text-accent-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold">•</span>
                    <span>Get custom quotes for bulk orders</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold">•</span>
                    <span>Discuss custom design requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold">•</span>
                    <span>Schedule a visit to our Experience Centre</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold">•</span>
                    <span>Get assistance with product selection</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold">•</span>
                    <span>Inquire about delivery and installation</span>
                  </li>
                </ul>

                <div className="mt-6 pt-6 border-t border-primary/30">
                  <p className="text-xs uppercase tracking-wider font-bold mb-2 text-accent-foreground">NEED IMMEDIATE ASSISTANCE?</p>
                  <p className="mono text-sm text-accent-foreground">Call: +91 95946 85818</p>
                  <p className="mono text-sm text-accent-foreground">Email: hello@modularfurniturestore.com</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border border-border rounded-none p-8">
              <h2 className="text-2xl font-black uppercase mb-6">SEND US A MESSAGE</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Full Name *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                  />
                </div>

                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                  />
                </div>

                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Phone Number</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                  />
                </div>

                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Subject *</Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="What is this regarding?"
                    className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                  />
                </div>

                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Message *</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us more about your requirements..."
                    className="bg-transparent border-2 border-border focus:border-primary rounded-none p-4 min-h-[150px]"
                  />
                </div>

                {/* File Attachment */}
                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Attach Premises Layout / Floor Plan</Label>
                  <p className="text-xs text-muted-foreground mb-3">Upload your premises dimensions, floor plan or layout (PDF, JPG, PNG - Max 10MB)</p>
                  
                  {!attachedFile ? (
                    <div className="border-2 border-dashed border-border rounded-none p-6 text-center hover:border-primary transition-colors cursor-pointer">
                      <input
                        type="file"
                        id="file-upload"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.dwg"
                        className="hidden"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm font-semibold">Click to upload file</p>
                        <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG, DOC, DWG</p>
                      </label>
                    </div>
                  ) : (
                    <div className="border-2 border-primary bg-primary/5 rounded-none p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-primary" />
                        <div>
                          <p className="font-semibold text-sm">{attachedFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(attachedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 hover:bg-red-100 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold py-6"
                >
                  {loading ? 'SENDING...' : 'SEND MESSAGE'}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  We typically respond within 24 hours
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-[#f5f0eb]">
        <PageFAQ pageKey="contact" />
        <RelatedPages pageKey="contact" className="mt-12" />
      </div>
    </div>
  );
}
